import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { 
  Users,
  FileText,
  TrendingUp,
  Globe,
  Briefcase,
  MessageSquare,
  Award,
  Loader2,
  BarChart3,
  Layers
} from 'lucide-react';
import { PatternsPanel } from '../../components/admin/PatternsPanel';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#07743F', '#72BF44', '#E4E517', '#58595B', '#0088FE', '#00C49F'];

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtext, color = 'bg-[#07743F]' }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Insight Card Component
const InsightCard = ({ icon, text }) => (
  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-[#07743F]/5 to-transparent rounded-xl">
    <span className="text-2xl">{icon}</span>
    <p className="text-gray-700">{text}</p>
  </div>
);

// Main Evidence Page
export const EvidencePage = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('metrics');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login', { state: { from: { pathname: '/admin/evidence' } } });
      return;
    }
    fetchMetrics();
  }, [isAdmin, navigate]);

  const fetchMetrics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/evidence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  // Prepare chart data
  const countryData = metrics?.stories_by_country 
    ? Object.entries(metrics.stories_by_country).map(([name, count]) => ({ name, count }))
    : [];

  const themeData = metrics?.stories_by_theme
    ? Object.entries(metrics.stories_by_theme).map(([name, value]) => ({ name, value }))
    : [];

  const statusData = metrics?.stories_by_status
    ? Object.entries(metrics.stories_by_status)
        .filter(([_, count]) => count > 0)
        .map(([name, value]) => ({ name: name.replace('_', ' '), value }))
    : [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-32 opacity-10">
          <img src="/images/evidence-greenhouse.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
        </div>
        <div className="container mx-auto px-4 py-4 relative z-10">
          <h1 className="text-2xl font-bold text-gray-900">Impact</h1>
          <p className="text-sm text-gray-600">
            Behind every number is a real person
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              data-testid="evidence-tab-metrics"
              onClick={() => setTab('metrics')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition ${
                tab === 'metrics'
                  ? 'border-[#07743F] text-[#07743F]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <BarChart3 className="w-4 h-4" /> Metrics
            </button>
            <button
              type="button"
              data-testid="evidence-tab-patterns"
              onClick={() => setTab('patterns')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition ${
                tab === 'patterns'
                  ? 'border-[#07743F] text-[#07743F]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Layers className="w-4 h-4" /> Patterns
            </button>
          </div>
        </div>
      </div>

      {tab === 'patterns' && (
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <h2 className="text-xl font-serif font-bold text-gray-900 mb-1">
              Cross-Story Pattern Analysis
            </h2>
            <p className="text-sm text-gray-600 max-w-3xl">
              Recurring barriers, opportunities, mobilisation narratives, and policy signals
              surfaced across all expanded stories — actionable evidence for partners and policy teams.
            </p>
          </div>
          <PatternsPanel token={token} />
        </div>
      )}

      {tab === 'metrics' && (
      <div className="container mx-auto px-4 py-8">
        {/* Youth Signals */}
        <section className="mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#07743F]" />
            Youth Signals
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={FileText} 
              label="Total Stories Submitted" 
              value={metrics?.total_stories_submitted || 0}
              color="bg-[#07743F]"
            />
            <StatCard 
              icon={Users} 
              label="Active Contributors" 
              value={metrics?.active_contributors || 0}
              subtext="Unique story authors"
              color="bg-[#72BF44]"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Published Stories" 
              value={metrics?.stories_by_status?.published || 0}
              color="bg-emerald-600"
            />
            <StatCard 
              icon={Award} 
              label="Stories in Use" 
              value={metrics?.stories_by_status?.used || 0}
              subtext="Distributed across channels"
              color="bg-purple-600"
            />
          </div>
        </section>

        {/* System Signals */}
        <section className="mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#07743F]" />
            System Signals
          </h2>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Stories by Country */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Stories by Country
                </CardTitle>
              </CardHeader>
              <CardContent>
                {countryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={countryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#07743F" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stories by Theme */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Stories by Theme
                </CardTitle>
              </CardHeader>
              <CardContent>
                {themeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={themeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {themeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Insights */}
          <div className="mt-6 space-y-3">
            {metrics?.most_common_barrier && (
              <InsightCard 
                icon="📊" 
                text={`"${metrics.most_common_barrier.charAt(0).toUpperCase() + metrics.most_common_barrier.slice(1)}" is the most common theme this month, indicating a key area of focus for youth in agriculture.`}
              />
            )}
            {metrics?.partner_responses > 0 && (
              <InsightCard 
                icon="🤝" 
                text={`${metrics.partner_responses} partner organization${metrics.partner_responses > 1 ? 's have' : ' has'} responded to youth story submissions.`}
              />
            )}
          </div>
        </section>

        {/* Policy Signals */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#07743F]" />
            Policy Signals
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <StatCard 
              icon={MessageSquare} 
              label="Policy Mentions" 
              value={metrics?.policy_mentions || 0}
              subtext="Referenced in discussions"
              color="bg-blue-600"
            />
            <StatCard 
              icon={Briefcase} 
              label="Briefing Usage" 
              value={metrics?.briefing_usage || 0}
              subtext="Used in policy briefs"
              color="bg-indigo-600"
            />
            <StatCard 
              icon={Award} 
              label="Impact Score" 
              value={(metrics?.total_stories_submitted || 0) > 0 ? 'Growing' : 'Baseline'}
              subtext="System learning status"
              color="bg-[#E4E517]"
            />
          </div>

          {/* Policy Insight */}
          <Card className="mt-6 bg-gradient-to-r from-[#07743F]/5 to-[#72BF44]/5 border-[#72BF44]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#07743F] rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">System Learning</h3>
                  <p className="text-gray-700">
                    Grow Your Tomorrow is actively collecting and amplifying youth voices from across Africa. 
                    As more stories flow through the pipeline, patterns emerge that inform policy recommendations 
                    and investment priorities. This dashboard provides real-time evidence of youth agricultural 
                    challenges and innovations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
      )}
    </div>
  );
};

export default EvidencePage;
